import { AppConfigKey } from "@/types/application";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { Label } from "@/components/ui/label";

interface AppConfigKeyEditorProps {
  configKey: AppConfigKey;
  onChange: (configKey: AppConfigKey) => void;
  onRemove: () => void;
}

export const AppConfigKeyEditor = ({ configKey, onChange, onRemove }: AppConfigKeyEditorProps) => {
  const handleKeyChange = (key: string) => {
    onChange({ ...configKey, key });
  };

  const handleTypeChange = (type: AppConfigKey['type']) => {
    let newValue: any = '';
    if (type === 'boolean') newValue = false;
    else if (type === 'number') newValue = 0;
    else if (type === 'array') newValue = [];
    
    onChange({ ...configKey, type, value: newValue });
  };

  const handleValueChange = (value: any) => {
    onChange({ ...configKey, value });
  };

  const handleArrayValueChange = (text: string) => {
    const arrayValue = text.split(',').map(item => item.trim()).filter(item => item !== '');
    onChange({ ...configKey, value: arrayValue });
  };

  const renderValueInput = () => {
    switch (configKey.type) {
      case 'boolean':
        return (
          <div className="flex items-center space-x-2">
            <Switch
              checked={configKey.value as boolean}
              onCheckedChange={handleValueChange}
            />
            <Label>{configKey.value ? 'True' : 'False'}</Label>
          </div>
        );
      
      case 'number':
        return (
          <Input
            type="number"
            value={configKey.value as number}
            onChange={(e) => handleValueChange(Number(e.target.value))}
            placeholder="Enter number"
          />
        );
      
      case 'array':
        return (
          <Input
            value={Array.isArray(configKey.value) ? configKey.value.join(', ') : ''}
            onChange={(e) => handleArrayValueChange(e.target.value)}
            placeholder="Comma-separated values"
          />
        );
      
      default:
        return (
          <Input
            value={configKey.value as string}
            onChange={(e) => handleValueChange(e.target.value)}
            placeholder="Enter value"
          />
        );
    }
  };

  return (
    <div className="border rounded-lg p-4 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 grid grid-cols-3 gap-4">
          <div>
            <Label>Key Name</Label>
            <Input
              value={configKey.key}
              onChange={(e) => handleKeyChange(e.target.value)}
              placeholder="key_name"
              className="font-mono"
            />
          </div>
          
          <div>
            <Label>Type</Label>
            <Select value={configKey.type} onValueChange={handleTypeChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="string">String</SelectItem>
                <SelectItem value="boolean">Boolean</SelectItem>
                <SelectItem value="number">Number</SelectItem>
                <SelectItem value="array">Array</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Value</Label>
            {renderValueInput()}
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={onRemove}
          className="mt-6"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      
      <div>
        <Label>Description (Optional)</Label>
        <Textarea
          value={configKey.description || ''}
          onChange={(e) => onChange({ ...configKey, description: e.target.value })}
          placeholder="What does this key do?"
          rows={2}
        />
      </div>
      
      <div className="flex items-center space-x-2">
        <Switch
          checked={configKey.required || false}
          onCheckedChange={(checked) => onChange({ ...configKey, required: checked })}
        />
        <Label>Required</Label>
      </div>
    </div>
  );
};
